/* Omni Wishlist — localStorage-only single wishlist
 * Storage: localStorage["omni_wishlist"] = JSON array of items
 *   each item: { handle, title, vendor, image, price, url }
 * Public API: window.OmniWishlist
 * Auto-behaviors:
 *   1. Scans .product.product-item cards and injects a heart toggle button
 *   2. PDP integration: any element with data-wishlist-toggle attribute becomes the toggle
 *   3. Wishlist page rendering: container with data-omni-wishlist-page is populated
 *   4. Header link badge: any element with data-omni-wishlist-count gets count text
 */
(function () {
  const KEY = 'omni_wishlist';

  function read() {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) { return []; }
  }
  function write(arr) {
    try { localStorage.setItem(KEY, JSON.stringify(arr)); } catch (e) {}
    document.dispatchEvent(new CustomEvent('omni:wishlist:update', { detail: { items: arr } }));
  }
  function has(handle) { return read().some(i => i.handle === handle); }
  function add(item) {
    const arr = read();
    if (arr.some(i => i.handle === item.handle)) return false;
    arr.push(item); write(arr); return true;
  }
  function remove(handle) {
    write(read().filter(i => i.handle !== handle));
  }
  function toggle(item) {
    if (has(item.handle)) { remove(item.handle); return false; }
    add(item); return true;
  }

  window.OmniWishlist = { read, has, add, remove, toggle };

  const HEART_OFF = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M12 21s-7.5-4.5-9.5-9.2C1 8.3 3.3 5 6.6 5c1.7 0 3.3.8 4.4 2.1A6 6 0 0 1 17.4 5c3.3 0 5.6 3.3 4 6.8C19.5 16.5 12 21 12 21z" fill="none" stroke="currentColor" stroke-width="2"/></svg>';
  const HEART_ON  = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M12 21s-7.5-4.5-9.5-9.2C1 8.3 3.3 5 6.6 5c1.7 0 3.3.8 4.4 2.1A6 6 0 0 1 17.4 5c3.3 0 5.6 3.3 4 6.8C19.5 16.5 12 21 12 21z" fill="currentColor"/></svg>';

  function paintBtn(btn, on) {
    btn.innerHTML = on ? HEART_ON : HEART_OFF;
    btn.classList.toggle('is-active', on);
    btn.setAttribute('aria-pressed', String(on));
    btn.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Add to wishlist');
    btn.setAttribute('title', on ? 'Remove from wishlist' : 'Add to wishlist');
  }

  function injectIntoProductCard(card) {
    if (card.dataset.wlInjected) return;
    card.dataset.wlInjected = '1';
    // card may itself be an <a> (e.g. .related-product-card) or contain a child link
    const link = card.matches('a[href*="/products/"]')
      ? card
      : card.querySelector('a[href*="/products/"]');
    if (!link) return;
    const handle = (link.getAttribute('href') || '').split('/products/')[1]?.split('?')[0]?.split('/')[0];
    if (!handle) return;
    const titleEl = card.querySelector('.product-name, .related-product-title');
    const brandEl = card.querySelector('.product-brand, .related-product-brand');
    const imgEl = card.querySelector('img');
    const priceEl = card.querySelector('.product-price-value, .related-product-price strong');
    const data = {
      handle,
      title: titleEl?.textContent.trim() || '',
      vendor: brandEl?.textContent.trim() || '',
      image: imgEl?.getAttribute('src') || '',
      price: priceEl?.textContent.trim() || '',
      url: link.getAttribute('href')
    };
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'omni-wishlist-heart';
    btn.dataset.wlHandle = handle;
    paintBtn(btn, has(handle));
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const nowOn = toggle(data);
      paintBtn(btn, nowOn);
    });
    card.style.position = card.style.position || 'relative';
    card.appendChild(btn);
  }

  function attachExplicitToggles(root) {
    (root || document).querySelectorAll('[data-wishlist-toggle]:not([data-wl-bound])').forEach(el => {
      el.dataset.wlBound = '1';
      const handle = el.dataset.wishlistHandle || el.dataset.handle;
      if (!handle) return;
      const data = {
        handle,
        title: el.dataset.wishlistTitle || '',
        vendor: el.dataset.wishlistVendor || '',
        image: el.dataset.wishlistImage || '',
        price: el.dataset.wishlistPrice || '',
        url: el.dataset.wishlistUrl || ('/products/' + handle)
      };
      paintBtn(el, has(handle));
      el.addEventListener('click', (e) => {
        e.preventDefault();
        const nowOn = toggle(data);
        paintBtn(el, nowOn);
      });
    });
  }

  function scanAll() {
    document.querySelectorAll('.product.product-item, .related-product-card').forEach(injectIntoProductCard);
    attachExplicitToggles();
    updateHeaderBadge();
  }

  function updateHeaderBadge() {
    const count = read().length;
    document.querySelectorAll('[data-omni-wishlist-count]').forEach(el => {
      el.textContent = count;
      el.toggleAttribute('hidden', count === 0);
    });
  }

  function renderWishlistPage() {
    const container = document.querySelector('[data-omni-wishlist-page]');
    if (!container) return;
    const items = read();
    const empty = container.querySelector('[data-omni-wishlist-empty]');
    const grid = container.querySelector('[data-omni-wishlist-grid]');
    if (items.length === 0) {
      empty.hidden = false;
      grid.hidden = true;
      grid.innerHTML = '';
      return;
    }
    empty.hidden = true;
    grid.hidden = false;
    const stars = '<span class="icon icon--ratingEmpty"><svg><use xlink:href="#icon-star"></use></svg></span>'.repeat(5);
    grid.innerHTML = items.map(it => {
      const alt = (it.title || '').replace(/"/g, '&quot;');
      const vendor = (it.vendor || '').replace(/"/g, '&quot;');
      return `
      <li class="product" data-wl-card-handle="${it.handle}">
        <article class="card">
          <button type="button" class="omni-wishlist-heart" aria-label="Remove from wishlist"
            data-wishlist-toggle data-wishlist-handle="${it.handle}" data-wishlist-title="${alt}"
            data-wishlist-vendor="${vendor}" data-wishlist-image="${it.image || ''}"
            data-wishlist-price="${(it.price || '').replace(/"/g, '&quot;')}" data-wishlist-url="${it.url}"></button>
          <figure class="card-figure">
            <a href="${it.url}">
              <div class="card-img-container img-hover-zoom">
                ${it.image ? `<img class="card-image" src="${it.image}" alt="${alt}" loading="lazy">` : '<div class="card-image"></div>'}
              </div>
            </a>
            <figcaption class="card-figcaption"><div class="card-figcaption-body"><div class="card-buttons card-buttons--alt"></div></div></figcaption>
          </figure>
          <div class="card-body">
            ${it.vendor ? `<p class="card-text card-text--brand">${it.vendor}</p>` : ''}
            <p class="card-text card-text--rating"><span class="rating">${stars}</span></p>
            <h4 class="card-title"><a href="${it.url}">${it.title || ''}</a></h4>
            <div class="card-text card-text--price">
              <div class="price-section price-section--withoutTax">
                <span class="price price--withoutTax price--main">${it.price || ''}</span>
              </div>
            </div>
          </div>
          <a class="button card-link" href="${it.url}">View Details</a>
        </article>
      </li>`;
    }).join('');
    attachExplicitToggles(grid);
  }

  document.addEventListener('omni:wishlist:update', () => {
    scanAll();
    renderWishlistPage();
  });

  function init() { scanAll(); renderWishlistPage(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  // Re-scan after morph/section render
  new MutationObserver(() => { scanAll(); }).observe(document.body, { childList: true, subtree: true });
})();
